/**
 * EvolutionChain - Display Pokemon evolution chain
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { usePokemon } from '../../hooks/usePokemon';
import { useApp } from '../../context/AppContext';
import { Skeleton } from '../ui/skeleton';
import { formatPokemonName } from '../../data/pokemonTranslations';

// Parse evolution chain data recursively
function parseEvolutionChain(chain) {
  const evolutions = [];
  
  function traverse(node, stage = 0) {
    const speciesName = node.species.name;
    const speciesId = node.species.url.split('/').filter(Boolean).pop();
    
    const evolutionDetails = node.evolution_details?.[0] || {};
    
    evolutions.push({
      id: parseInt(speciesId),
      name: speciesName,
      stage,
      trigger: evolutionDetails.trigger?.name,
      minLevel: evolutionDetails.min_level,
      item: evolutionDetails.item?.name,
      heldItem: evolutionDetails.held_item?.name,
      timeOfDay: evolutionDetails.time_of_day,
      minHappiness: evolutionDetails.min_happiness,
      minAffection: evolutionDetails.min_affection,
      location: evolutionDetails.location?.name,
      knownMove: evolutionDetails.known_move?.name,
      knownMoveType: evolutionDetails.known_move_type?.name
    });
    
    node.evolves_to.forEach(evolution => {
      traverse(evolution, stage + 1);
    });
  }
  
  traverse(chain);
  return evolutions;
}

function EvolutionPokemon({ evolution }) {
  const { getPokemon, getSprites } = usePokemon();
  const { language } = useApp();
  const [pokemon, setPokemon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  useEffect(() => {
    let mounted = true;
    
    const fetchPokemon = async () => {
      const data = await getPokemon(evolution.id);
      if (mounted) {
        setPokemon(data);
        setLoading(false);
      }
    };
    
    fetchPokemon();
    return () => { mounted = false; };
  }, [evolution.id, getPokemon]);
  
  const sprites = pokemon ? getSprites(pokemon) : null;
  
  // Format evolution condition
  const getEvolutionCondition = () => {
    const conditions = [];
    
    if (evolution.minLevel) {
      conditions.push(`Lv. ${evolution.minLevel}`);
    }
    if (evolution.item) {
      conditions.push(evolution.item.replace(/-/g, ' '));
    }
    if (evolution.heldItem) {
      conditions.push(`Hold ${evolution.heldItem.replace(/-/g, ' ')}`);
    }
    if (evolution.timeOfDay) {
      conditions.push(evolution.timeOfDay === 'day' ? '☀️' : '🌙');
    }
    if (evolution.minHappiness) {
      conditions.push('💕 Happiness');
    }
    if (evolution.location) {
      conditions.push(`@ ${evolution.location.replace(/-/g, ' ')}`);
    }
    if (evolution.knownMove) {
      conditions.push(`Know ${evolution.knownMove.replace(/-/g, ' ')}`);
    }
    
    return conditions.length > 0 ? conditions.join(' + ') : '';
  };
  
  if (loading) {
    return (
      <div className="flex flex-col items-center">
        <Skeleton className="w-20 h-20 rounded-full" />
        <Skeleton className="w-16 h-4 mt-2" />
      </div>
    );
  }

  const condition = getEvolutionCondition();

  return (
    <Link
      to={`/pokemon/${evolution.id}`}
      className="flex flex-col items-center group"
      data-testid={`evolution-pokemon-${evolution.id}`}
    >
      <motion.div
        whileHover={{ scale: 1.1 }}
        className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-violet-100 to-rose-100 dark:from-violet-900/50 dark:to-rose-900/50 flex items-center justify-center overflow-hidden"
      >
        {!imageLoaded && (
          <Skeleton className="absolute inset-0 rounded-full" />
        )}
        <img
          src={sprites?.default}
          alt={pokemon?.name}
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
          className={`w-14 h-14 sm:w-16 sm:h-16 object-contain transition-opacity ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      </motion.div>
      <span className="mt-1 text-xs sm:text-sm font-medium text-foreground group-hover:text-violet-600 transition-colors truncate max-w-[80px]">
        {formatPokemonName(pokemon?.name, language)}
      </span>
      <span className="text-[10px] text-muted-foreground">
        #{String(evolution.id).padStart(3, '0')}
      </span>
      {condition && (
        <span className="mt-0.5 text-[9px] sm:text-[10px] text-violet-600 dark:text-violet-400 text-center max-w-[80px] leading-tight">
          {condition}
        </span>
      )}
    </Link>
  );
}

export function EvolutionChain({ speciesData }) {
  const { t, language } = useApp();
  const { getEvolutionChain } = usePokemon();
  const [evolutions, setEvolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    let mounted = true;
    
    const fetchEvolutionChain = async () => {
      if (!speciesData?.evolution_chain?.url) {
        setLoading(false);
        return;
      }
      
      const chain = await getEvolutionChain(speciesData.evolution_chain.url);
      if (mounted && chain) {
        const parsed = parseEvolutionChain(chain.chain);
        setEvolutions(parsed);
      }
      setLoading(false);
    };
    
    fetchEvolutionChain();
    return () => { mounted = false; };
  }, [speciesData, getEvolutionChain]);
  
  if (loading) {
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-bold text-foreground">{t('evolution')}</h3>
        <div className="flex items-center justify-center gap-4 p-4">
          <Skeleton className="w-20 h-20 rounded-full" />
          <Skeleton className="w-6 h-6" />
          <Skeleton className="w-20 h-20 rounded-full" />
        </div>
      </div>
    );
  }
  
  if (evolutions.length <= 1) {
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-bold text-foreground">{t('evolution')}</h3>
        <p className="text-sm text-muted-foreground text-center py-4">
          {language === 'es' ? 'Este Pokémon no evoluciona' : 'This Pokémon does not evolve'}
        </p>
      </div>
    );
  }
  
  // Group by stage so branched chains (Eevee, Wurmple, Tyrogue…) render as
  // separate columns of siblings instead of one misleading linear chain.
  const stageGroups = [];
  evolutions.forEach(evo => {
    const s = evo.stage ?? 0;
    if (!stageGroups[s]) stageGroups[s] = [];
    stageGroups[s].push(evo);
  });
  const groups = stageGroups.filter(Boolean);

  return (
    <div className="space-y-3" data-testid="evolution-chain">
      <h3 className="text-lg font-bold text-foreground">{t('evolution')}</h3>
      <div className="flex flex-wrap items-center justify-center gap-1 p-2 sm:p-4 bg-muted/50 rounded-xl">
        {groups.map((group, gIdx) => (
          <div key={gIdx} className="flex items-center gap-1 sm:gap-2">
            {gIdx > 0 && (
              <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-violet-500 shrink-0" />
            )}
            <div className="flex flex-col gap-2 sm:gap-3">
              {group.map(evo => (
                <EvolutionPokemon key={evo.id} evolution={evo} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default EvolutionChain;
